// plp.js — sidebar filters + active chips + rich sort + URL state + mobile sheet
//
// State shape:
//   { q, categories: Set, styles: Set, colors: Set, sizes: Set, materials: Set,
//     priceMin, priceMax, onSale, isNew, sort, page }

import { cart, registerQuickPool } from './app.js?v=31';
import { t, LOCALE } from './i18n.js?v=13';
import { inRoom, inMood, inColor, relevantie, roomById, moodById } from './finder-config.js?v=7';
const LP = LOCALE === 'nl' ? '' : '/' + LOCALE;
import { loadProducts, loadAllProducts, fmt, COLOR_BUCKETS, colorBucketOf, CATEGORY_LABELS, STYLE_LABELS, findVariant } from './data.js?v=23';

const ADD_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';
const CHEV = '<svg class="chev" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"/></svg>';

const PAGE_SIZE = 12;
// Toon het review-aantal (· N) pas vanaf dit aantal; daaronder alleen sterren+cijfer
// (voorkomt "· 1" naast "49 verkocht deze maand", wat ongeloofwaardig oogt).
const REVIEWS_MIN = 5;

let allProducts = [];
  registerQuickPool(allProducts);
let priceBounds = { min: 14, max: 50 };

const state = {
  q: '',
  categories: new Set(),
  styles:     new Set(),
  colors:     new Set(),
  room:       null,   // Posterzoeker (home): ruimte-id uit finder-config
  mood:       null,   // Posterzoeker: sfeer-id
  sizes:      new Set(),
  materials:  new Set(),
  priceMin:   14,
  priceMax:   50,
  onSale:     false,
  isNew:      false,
  sort:       'recommended',
  page:       1,
};

// ============================================================
// FILTER GROUPS — definition drives both desktop sidebar + mobile sheet
// ============================================================
function buildGroups() {
  // Each poster is available in alle XS/S/M/L sizes + paper/dibond/garden materials,
  // so size/material filters are not useful. Style overlaps with Categorie (Popart,
  // Vintage, Minimalistisch are categories). What remains:
  //   - Snel filter (sale + nieuw, top priority)
  //   - Categorie (multi-cat metafield, ~31 opties)
  //   - Kleur (swatches from shopify.color-pattern metafield)
  //   - Prijs (slider)
  return [
    {
      id: 'flags',
      label: t('flt_quick'),
      type: 'switches',
      options: [
        { id: 'onSale', label: t('flt_sale') },
        { id: 'isNew',  label: t('flt_new') },
      ],
    },
    {
      id: 'categories',
      label: t('flt_cat'),
      type: 'check',
      options: countOptions('category', CATEGORY_LABELS),
    },
    {
      id: 'colors',
      label: t('flt_color'),
      type: 'swatch',
      options: colorOptions(),
    },
  ];
}

// Compute the "intersection set" — all products passing ALL filters EXCEPT the
// given group. Used to make counts dynamic so users see what would remain.
function intersectionExcluding(group) {
  return applyFilters(group);
}

// For multi-value fields (categories list), count by EACH value the product has,
// not just the primary `category`.
function countOptions(field, labelMap) {
  const set = intersectionExcluding('categories');
  const counts = {};
  for (const p of set) {
    // multi-cat support: tel per unieke categorie, case-insensitief ontdubbeld.
    // De primaire `category` is lowercase maar staat als Title-Case óók in
    // categories[] ("voetbal" + "Voetbal") — zonder lowercasen vóór het dedupen
    // werd elk product dubbel geteld voor z'n primaire categorie.
    const raw = field === 'category'
      ? [p.category, ...(p.categories || [])]
      : [p[field]];
    const values = new Set(raw.map(v => (v || '').toLowerCase()).filter(Boolean));
    for (const k of values) {
      // 'dibond' is een materiaal-collectie, geen categorie — hoort niet in dit filter
      if (k === 'dibond') continue;
      counts[k] = (counts[k] || 0) + 1;
    }
  }
  return Object.entries(counts)
    .map(([id, count]) => ({ id, label: labelMap[id] || prettyLabel(id), count }))
    .sort((a, b) => b.count - a.count);
}
// Pretty-label fallback: 'natuur en landschappen' → 'Natuur en landschappen'
function prettyLabel(id) {
  return id.charAt(0).toUpperCase() + id.slice(1);
}

function countSized(code) {
  return intersectionExcluding('sizes').filter(p => (p.sizeCodes || []).includes(code)).length;
}
function countMaterial(code) {
  return intersectionExcluding('materials').filter(p => (p.materialCodes || []).includes(code)).length;
}

// ============================================================
// Shopify storefront pagination helpers.
// Shape that Shopify's products.json returns, mapped to what data.js expects.
// ============================================================
function shopifyToOurShape(p) {
  return {
    id:        p.handle,
    handle:    p.handle,
    title:     p.title,
    img:       p.featured_image || (p.images && p.images[0]),
    price_from: Number(p.price_min ?? p.price ?? 0),
    originalPrice: Number(p.compare_at_price_min ?? 0), // min bij min: nooit meer de dibond-L-vanafprijs doorstrepen naast de XS-prijs
    tags:      typeof p.tags === 'string' ? p.tags.split(', ') : (p.tags || []),
    category:  '',           // metafields not in products.json
    categories: [],
    available: p.available,
    rating:    4.8,
    review_count: 0,
    vendor:    p.vendor,
    variants:  (p.variants || []).map(v => ({
      id: v.id, sku: v.sku || null,
      size: v.option2 || null, material: v.option1 || null,
      price: Number(v.price ?? p.price ?? 0), available: v.available !== false,
    })),
    colors: [], color_dominant: '',
  };
}
// Normalize again using our data.js logic (size codes, material codes, etc.)
function normalizeFromShopifyShape(p) {
  // Re-use a minimal normalize — duplicated inline because data.js's normalize is private.
  const SIZE_MAP = {'30x40cm':'XS','50x70cm':'S','60x90cm':'M','70x100cm':'L','30x40':'XS','50x70':'S','60x90':'M','70x100':'L','51x71':'S','61x91':'M','71x101':'L'};
  const MAT_MAP  = {'heavycoated matte paper':'paper','met witte rand':'paper','aluminium dibond':'dibond','dibond':'dibond','tuinposter pvc':'garden','pvc':'garden'};
  const variants = (p.variants || []).map(v => ({
    ...v,
    sizeCode: (v.size && (SIZE_MAP[v.size.toLowerCase().replace(/\s+/g,'')] || SIZE_MAP[v.size.toLowerCase().replace(/\s+/g,'').replace(/cm$/,'')])) || null,
    materialCode: (v.material && MAT_MAP[v.material.toLowerCase()]) || null,
  }));
  return {
    ...p,
    image:   p.img || '',
    price:   p.price_from,
    onSale:  p.originalPrice > p.price_from,
    bestsellerRank: 9999,
    isBestseller: false,
    isNew: (p.tags || []).map(t => t.toLowerCase()).includes('new')
      || (p.publishedAt && (Date.now() / 1000 - p.publishedAt) < 30 * 86400),
    variants,
    sizeCodes:     [...new Set(variants.map(v => v.sizeCode).filter(Boolean))],
    materialCodes: [...new Set(variants.map(v => v.materialCode).filter(Boolean))],
    color: '',
    colors: [],
  };
}

function colorOptions() {
  const set = intersectionExcluding('colors');
  const counts = {};
  for (const p of set) {
    // zelfde kleurregel als het filter (finder-config inColor: gemeten aandeel >= 15%)
    for (const b of COLOR_BUCKETS) if (inColor(p, b.id)) counts[b.id] = (counts[b.id] || 0) + 1;
  }
  return COLOR_BUCKETS
    .filter(b => counts[b.id])
    .map(b => ({ id: b.id, label: b.label, count: counts[b.id], hex: b.hex }));
}

// ============================================================
// RENDER SIDEBAR + SHEET (same content, different containers)
// ============================================================
function renderGroups() {
  const groups = buildGroups();
  const html = groups.map(g => renderGroup(g)).join('') + clearAllHTML();
  const side = document.getElementById('filter-sidebar');
  const sheet = document.getElementById('filter-sheet-body');
  if (side) side.innerHTML = html;
  if (sheet) sheet.innerHTML = html;
  syncFromState();
  refreshFabBadge();
}

function renderGroup(g) {
  if (g.type === 'check') {
    const activeCount = state[g.id]?.size || 0;
    const showSearch = g.options.length > 8;
    const initial = activeCount > 0 || g.options.length <= 8 ? g.options.length : 6;
    return `
      <div class="filter-group ${activeCount ? 'is-active' : ''}" data-open="true" data-group="${g.id}">
        <button class="filter-group__head" type="button" data-action="toggle-group">
          <span>${g.label}${activeCount ? ` <span class="fg-active-pill tabular">${activeCount}</span>` : ''}</span>${CHEV}
        </button>
        <div class="filter-group__body">
          ${showSearch ? `
            <input class="fg-search" type="search" placeholder="${t('search_in', {g: g.label.toLowerCase()})}" data-fg-search="${g.id}" />
          ` : ''}
          <div class="fg-options" data-fg-options="${g.id}">
            ${g.options.map((o, i) => `
              <label class="fopt" data-fg-item="${(o.label || '').toLowerCase()}" ${i >= initial ? 'hidden' : ''}>
                <input type="checkbox" data-group-id="${g.id}" data-opt-id="${o.id}" />
                <span>${o.label}</span>
                <span class="fopt__count tabular">${o.count}</span>
              </label>
            `).join('')}
          </div>
          ${g.options.length > initial ? `
            <button class="fg-showall" type="button" data-fg-showall="${g.id}">${t('show_more', {n: g.options.length - initial})}</button>
          ` : ''}
        </div>
      </div>
    `;
  }
  if (g.type === 'swatch') {
    return `
      <div class="filter-group" data-open="true" data-group="${g.id}">
        <button class="filter-group__head" type="button" data-action="toggle-group">
          <span>${g.label}</span>${CHEV}
        </button>
        <div class="filter-group__body">
          <div class="swatch-grid">
            ${g.options.map(o => `
              <button class="swatch" style="color:${o.hex}" data-group-id="${g.id}" data-opt-id="${o.id}" aria-pressed="false" aria-label="${o.label} (${o.count})" title="${o.label} · ${o.count}">
                <span class="sr-only">${o.label}</span>
              </button>
            `).join('')}
          </div>
        </div>
      </div>
    `;
  }
  if (g.type === 'price') {
    return `
      <div class="filter-group" data-open="true" data-group="price">
        <button class="filter-group__head" type="button" data-action="toggle-group">
          <span>${g.label}</span>${CHEV}
        </button>
        <div class="filter-group__body">
          <div class="price-slider">
            <div class="price-slider__values">
              <span>€<span class="tabular" data-price-min>${state.priceMin}</span></span>
              <span>€<span class="tabular" data-price-max>${state.priceMax}</span></span>
            </div>
            <div class="price-slider__rails">
              <div class="price-slider__track"></div>
              <div class="price-slider__fill" data-price-fill></div>
              <input type="range" min="${priceBounds.min}" max="${priceBounds.max}" step="1" value="${state.priceMin}" data-price-input="min" />
              <input type="range" min="${priceBounds.min}" max="${priceBounds.max}" step="1" value="${state.priceMax}" data-price-input="max" />
            </div>
          </div>
        </div>
      </div>
    `;
  }
  if (g.type === 'switches') {
    return `
      <div class="filter-group" data-open="true" data-group="${g.id}">
        <button class="filter-group__head" type="button" data-action="toggle-group">
          <span>${g.label}</span>${CHEV}
        </button>
        <div class="filter-group__body">
          ${g.options.map(o => `
            <label class="fswitch">
              <span>${o.label}</span>
              <input type="checkbox" data-flag-id="${o.id}" />
              <span class="fswitch__track"></span>
            </label>
          `).join('')}
        </div>
      </div>
    `;
  }
  return '';
}

function clearAllHTML() {
  return `<button class="filter-clear" data-action="clear-filters">${t('clear_filters')}</button>`;
}

// ============================================================
// FILTER + SORT ENGINE
// ============================================================
// Single source of truth for each filter predicate. Used by applyFilters() and
// by dynamic count computation (count-after-excluding-this-group).
// Sfeerwoorden uit de AI-search vertalen naar termen die echt in de data
// voorkomen — anders geeft "rustige abstract" 0 resultaten omdat "rustig"
// in geen enkele titel of tag staat.
const Q_ALIASES = {
  // NB: 'abstract' hoort hier bewust NIET bij — anders matcht "rustige abstract"
  // ook voetballers/celebs met "Abstract" in de titel.
  rustig:    ['minimalistisch', 'zen', 'aquarel', 'pastel', 'natuur'],
  kalm:      ['minimalistisch', 'zen', 'aquarel', 'pastel', 'natuur'],
  stedelijk: ['steden', 'stad', 'skyline', 'city', 'urban'],
  urban:     ['steden', 'stad', 'skyline', 'city'],
  fel:       ['pop art', 'neon', 'kleurrijk'],
  bold:      ['pop art', 'neon', 'kleurrijk', 'abstract'],
  muziek:    ['muzikanten', 'muziek'],
  film:      ['films', 'film', 'series'],
  sport:     ['voetbal', 'atleten', 'sport'],
};
const FILTER_PREDICATES = {
  q: (p, st) => {
    if (!st.q) return true;
    const tokens = st.q.toLowerCase().split(/\s+/).filter(Boolean);
    const hay = `${p.title} ${p.category} ${(p.categories||[]).join(' ')} ${p.style} ${(p.tags||[]).join(' ')} ${p.handle || ''}`.toLowerCase();
    const cats = (p.categories || []).map(c => String(c).toLowerCase());
    if (p.category) cats.push(String(p.category).toLowerCase());
    return tokens.every(t => {
      const stem = t.replace(/(en|e|s)$/, '');
      // Is het zoekwoord exact een categorienaam? Dan telt alleen het LABEL —
      // anders matcht "abstract" ook "Frida Kahlo Abstract" via de titel,
      // terwijl de labels nu juist accuraat zijn gemaakt.
      const isCat = CATEGORY_LABELS[t] || CATEGORY_LABELS[stem];
      if (isCat) return cats.includes(t) || cats.includes(stem);
      if (hay.includes(t)) return true;
      if (stem.length >= 4 && hay.includes(stem)) return true;
      const al = Q_ALIASES[t] || Q_ALIASES[stem];
      return al ? al.some(a => hay.includes(a)) : false;
    });
  },
  categories: (p, st) => {
    if (!st.categories.size) return true;
    // Filter-ids zijn lowercase; de primaire category is lowercase maar categories[]
    // is Title-Case. Beide zijden lowercasen, anders matchen secundaire labels nooit
    // en geeft het filter minder resultaten dan de getoonde count.
    if (st.categories.has((p.category || '').toLowerCase())) return true;
    return (p.categories || []).some(c => st.categories.has((c || '').toLowerCase()));
  },
  styles: (p, st) => !st.styles.size || st.styles.has(p.style),
  room:   (p, st) => !st.room || inRoom(p, st.room),
  mood:   (p, st) => !st.mood || inMood(p, st.mood),
  colors: (p, st) => {
    if (!st.colors.size) return true;
    return [...st.colors].some(id => inColor(p, id));
  },
  sizes:     (p, st) => !st.sizes.size     || (p.sizeCodes || []).some(c => st.sizes.has(c)),
  materials: (p, st) => !st.materials.size || (p.materialCodes || []).some(c => st.materials.has(c)),
  price:     (p, st) => p.price >= st.priceMin && p.price <= st.priceMax,
  flags:     (p, st) => (!st.onSale || p.onSale) && (!st.isNew || p.isNew),
};

function applyFilters(except = null) {
  let list = allProducts.slice();
  for (const [key, fn] of Object.entries(FILTER_PREDICATES)) {
    if (key === except) continue;
    list = list.filter(p => fn(p, state));
  }

  // sort
  switch (state.sort) {
    case 'bestsellers':
      list.sort((a, b) => a.bestsellerRank - b.bestsellerRank);
      break;
    case 'newest':
      list.sort((a, b) => Number(b.isNew) - Number(a.isNew));
      break;
    case 'price-asc':
      list.sort((a, b) => a.price - b.price);
      break;
    case 'price-desc':
      list.sort((a, b) => b.price - a.price);
      break;
    case 'rating':
      list.sort((a, b) => b.rating - a.rating);
      break;
    case 'reviews':
      list.sort((a, b) => b.reviews - a.reviews);
      break;
    default: { // 'recommended' — bij ruimte/sfeer/kleur eerst de sterkste match, dan magnet, bestsellerRank, rating
      const kleur = state.colors.size === 1 ? [...state.colors][0] : null;
      const sel = { room: state.room, mood: state.mood, color: kleur };
      const rel = (state.room || state.mood || kleur) ? new Map(list.map(p => [p, relevantie(p, sel)])) : null;
      list.sort((a, b) => {
        if (rel) { const d = rel.get(a) - rel.get(b); if (Math.abs(d) > 0.05) return d; }
        if (a.isMagnet !== b.isMagnet) return a.isMagnet ? -1 : 1;
        if (a.bestsellerRank !== b.bestsellerRank) return a.bestsellerRank - b.bestsellerRank;
        return b.rating - a.rating;
      });
    }
  }
  return list;
}

// ============================================================
// CARD RENDER
// ============================================================
// Volgorde van de server-render (Shopify "best verkocht") vastleggen vóór de eerste JS-render. Sleutel = bestandsnaam
// van het beeld: die is in elke taal gelijk (handles en URL's zijn vertaald). Zonder dit verving het script de eerste
// rij door ándere producten, waardoor het grootste beeld opnieuw en pas laat werd opgehaald (LCP 13-15 s gemeten).
const _imgKey = (u) => String(u || '').split('?')[0].split('/').pop();
const SSR_ORDER = new Map([...document.querySelectorAll('#plp-grid > a.p-card img')].map((im, i) => [_imgKey(im.getAttribute('src')), i]));
function ssrVolgorde(list) {
  if (!SSR_ORDER.size || state.sort !== 'recommended' || activeFilterCount() > 0) return list;
  const r = (p) => { const k = SSR_ORDER.get(_imgKey(p.image)); return k === undefined ? 1e9 : k; };
  return [...list].sort((x, y) => r(x) - r(y));
}
function cardHTML(p, idx) {
  // De eerste kaarten staan (bijna) in beeld: direct laden met voorrang. Alles stond op lazy, waardoor het
  // grootste beeld van de collectiepagina pas laat werd opgehaald (LCP-meting 14 s onder mobiele throttling).
  const eerste = typeof idx === 'number' && idx < 2;
  const sale = p.onSale;
  // Prioriteit: Bestseller > Nieuw > Sale — sinds de catalogusbrede korting heeft
  // alles een sale, dus die badge is de minst onderscheidende.
  const badge = p.isBestseller ? `<span class="p-card__badge badge-bestseller">${t('badge_bestseller')}</span>`
              : p.isNew ? `<span class="p-card__badge badge-new">${t('badge_new')}</span>`
              : sale ? `<span class="p-card__badge badge-sale">${t('badge_sale')}</span>` : '';
  // "Verkocht deze maand" verwijderd 25-09-2026 (Wes): overbodig op de kaarten.
  return `
    <div class="p-card-wrap" style="position: relative;">
      <a class="p-card" href="${LP}/products/${p.id}">
        <div class="p-card__img">
          <img src="${eerste ? String(p.image).replace(/([?&]width=)\d+/, '$1600').replace(/^https?:\/\/cdn\.shopify\.com\/s\/files\/1\/\d+\/\d+\/\d+\/files\//, '/cdn/shop/files/') : p.image}" alt="${p.title}" ${eerste ? 'loading="eager" fetchpriority="high"' : 'loading="lazy"'} width="480" height="600" />
          ${badge}
        </div>
        <div class="p-card__body">
          <span class="p-card__eyebrow label">${CATEGORY_LABELS[p.category] || p.category || t('cat_poster')}</span>
          <h3 class="p-card__title">${p.title}</h3>
          <div class="p-card__row">
            <span class="p-card__price">${fmt(p.price)}${sale ? ` <del>${fmt(p.originalPrice)}</del>` : ''}</span>
            <span class="p-card__stars stars" aria-label="${Number(p.rating).toFixed(1)} van 5 sterren">
              <span class="rating-stars" style="--pct:${(Number(p.rating)/5*100).toFixed(1)}%"><span class="rating-stars__fill">★★★★★</span></span>
              <span class="tabular">${Number(p.rating).toFixed(1)}</span>
            </span>
          </div>
        </div>
      </a>
      <button class="p-card__quick" aria-label="${t('card_add_aria', { title: p.title })}" data-quick-add="${p.id}">${ADD_ICON}</button>
    </div>
  `;
}

// ============================================================
// RENDER

// Genummerde paginanavigatie (Wes 03-09): 1 2 3 4 … N naast de laad-meer-knop.
// "Pagina p" = alles t/m p zichtbaar (zelfde model als laad-meer, dus de twee
// bedieningen spreken elkaar nooit tegen).
function renderPageNumbers(total) {
  const wrap = document.getElementById('load-more-wrap');
  if (!wrap) return;
  let nav = document.getElementById('plp-pages');
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) { if (nav) nav.remove(); return; }
  if (!nav) {
    nav = document.createElement('nav');
    nav.id = 'plp-pages';
    nav.className = 'plp-pages';
    nav.setAttribute('aria-label', 'Paginering');
    wrap.parentNode.insertBefore(nav, wrap.nextSibling);
  }
  const cur = Math.min(state.page, pages);
  const toon = new Set([1, 2, 3, 4, pages, cur - 1, cur, cur + 1].filter(p => p >= 1 && p <= pages));
  const delen = [...toon].sort((a, b) => a - b);
  let html = '', vorige = 0;
  for (const p of delen) {
    if (p - vorige > 1) html += '<span class="plp-pages__dots">…</span>';
    html += `<button class="plp-pages__nr${p === cur ? ' is-actief' : ''}" data-page="${p}">${p}</button>`;
    vorige = p;
  }
  nav.innerHTML = html;
  nav.querySelectorAll('button').forEach(b => b.addEventListener('click', () => {
    state.page = Number(b.dataset.page);
    render();
    document.getElementById('plp-grid').scrollIntoView({ behavior: 'smooth', block: 'start' });
  }));
}

// ============================================================
// Tot de volledige catalogus binnen is, kennen we alleen de 36 server-side posters. Met een actief filter
// zou dat "0 resultaten" geven terwijl er honderden zijn (bv. vanuit de Posterzoeker): toon dan een laadstatus.
let catalogusKlaar = false;
function renderLaden() {
  const grid = document.getElementById('plp-grid');
  grid.hidden = false;
  grid.innerHTML = Array.from({ length: 8 }, () => '<div class="plp-skel" aria-hidden="true"><span></span><i></i><i></i></div>').join('');
  document.getElementById('plp-empty').hidden = true;
  document.getElementById('load-more-wrap').hidden = true;
  document.getElementById('results-count').innerHTML = `<span class="plp-zoekt">${t('fz_loading')}</span>`;
  renderActiveChips();
  refreshFabBadge();
  writeUrlState();
}

function render() {
  // Filter gezet terwijl de catalogus nog niet geladen is: nu starten (ook als er geen aanraking-event was,
  // bv. bediening via schermlezer of script), anders blijft de laadstatus staan.
  if (!catalogusKlaar && activeFilterCount() > 0) { if (window.__plpStartCatalogus) window.__plpStartCatalogus(); renderLaden(); return; }
  const list = ssrVolgorde(applyFilters());
  const visible = list.slice(0, state.page * PAGE_SIZE);

  const grid = document.getElementById('plp-grid');
  const empty = document.getElementById('plp-empty');
  const loadWrap = document.getElementById('load-more-wrap');
  const countEl = document.getElementById('results-count');
  const sheetApply = document.getElementById('sheet-apply-label');

  // Bestaande afbeeldingen hergebruiken: wie al in beeld stond (server-kaarten of vorige render) behoudt zijn
  // <img>-element in de nieuwe kaart. Anders maakt elke render nieuwe elementen en telt de browser het eerste
  // grote beeld opnieuw als "grootste element" op het moment van de late catalogus-render.
  const _oud = new Map([...grid.querySelectorAll('.p-card__img > img')].map(im => [_imgKey(im.getAttribute('src')), im]));
  grid.innerHTML = visible.map(cardHTML).join('');
  if (_oud.size) {
    grid.querySelectorAll('.p-card__img > img').forEach((im, i) => {
      if (i >= 12) return;
      const o = _oud.get(_imgKey(im.getAttribute('src')));
      if (o && o.complete && o.naturalWidth > 0) { o.alt = im.alt; im.replaceWith(o); _oud.delete(_imgKey(o.getAttribute('src'))); }
    });
  }
  // Crawlbare paginanavigatie (server-side) is voor wie zonder JS komt; met "meer laden" overbodig.
  // Op een diepe ?page=N laten we hem staan, zodat je daar nog verder kunt bladeren.
  const pagesNav = document.getElementById('plp-pages');
  if (pagesNav && !/[?&]page=([2-9]|\d{2,})/.test(location.search)) pagesNav.hidden = true;
  empty.hidden = list.length > 0;
  grid.hidden = list.length === 0;
  if (list.length === 0) renderEmptySuggestions();
  loadWrap.hidden = visible.length >= list.length && state.page === 1;
  renderPageNumbers(list.length);
  countEl.innerHTML = `<strong class="tabular">${list.length}</strong> ${t('results')}`;
  if (sheetApply) sheetApply.textContent = t('show_results', {n: list.length});

  renderActiveChips();
  refreshFabBadge();
  writeUrlState();
}

// Populaire posters tonen bij nul filterresultaten (geen doodlopende pagina).
function renderEmptySuggestions() {
  const wrap = document.getElementById('plp-empty-suggest');
  const grid = document.getElementById('plp-empty-grid');
  if (!wrap || !grid) return;
  if (grid.childElementCount) { wrap.hidden = false; return; } // al gevuld
  const picks = [...allProducts]
    .filter(p => p.available !== false)
    .sort((a, b) => (a.bestsellerRank ?? 9999) - (b.bestsellerRank ?? 9999))
    .slice(0, 4);
  if (!picks.length) return;
  grid.innerHTML = picks.map(cardHTML).join('');
  wrap.hidden = false;
}

function renderActiveChips() {
  const chips = [];
  if (state.q) chips.push({ kind: 'q', label: `"${state.q}"`, payload: 'q' });
  if (state.room && roomById(state.room)) chips.push({ kind: 'room', label: t('room_' + state.room), payload: 'room' });
  if (state.mood && moodById(state.mood)) chips.push({ kind: 'mood', label: t('mood_' + state.mood), payload: 'mood' });
  state.categories.forEach(id => chips.push({ kind: 'categories', label: CATEGORY_LABELS[id] || id, payload: id }));
  state.styles.forEach(id => chips.push({ kind: 'styles', label: STYLE_LABELS[id] || id, payload: id }));
  state.colors.forEach(id => {
    const b = COLOR_BUCKETS.find(x => x.id === id);
    chips.push({ kind: 'colors', label: b?.label || id, payload: id });
  });
  state.sizes.forEach(id => chips.push({ kind: 'sizes', label: id, payload: id }));
  state.materials.forEach(id => chips.push({ kind: 'materials', label: id, payload: id }));
  if (state.priceMin > priceBounds.min || state.priceMax < priceBounds.max) {
    chips.push({ kind: 'price', label: `€${state.priceMin} - €${state.priceMax}`, payload: 'price' });
  }
  if (state.onSale) chips.push({ kind: 'flags', label: t('chip_sale'), payload: 'onSale' });
  if (state.isNew)  chips.push({ kind: 'flags', label: t('chip_new'), payload: 'isNew' });

  const el = document.getElementById('active-chips');
  el.innerHTML = chips.map(c =>
    `<button class="active-chip" data-remove-kind="${c.kind}" data-remove-payload="${c.payload}">${c.label}</button>`
  ).join('');
}

function refreshFabBadge() {
  const count = activeFilterCount();
  const el = document.getElementById('filter-fab-count');
  if (!el) return;
  if (count > 0) { el.textContent = count; el.hidden = false; }
  else { el.hidden = true; }
}

function activeFilterCount() {
  return state.categories.size + state.styles.size + state.colors.size +
    state.sizes.size + state.materials.size +
    (state.onSale ? 1 : 0) + (state.isNew ? 1 : 0) + (state.room ? 1 : 0) + (state.mood ? 1 : 0) +
    (state.priceMin > priceBounds.min || state.priceMax < priceBounds.max ? 1 : 0);
}

// ============================================================
// SYNC inputs from state (after URL load or reset)
// ============================================================
function syncFromState() {
  document.querySelectorAll('[data-group-id]').forEach(el => {
    const g = el.dataset.groupId;
    const id = el.dataset.optId;
    if (el.tagName === 'BUTTON') {
      // swatch
      el.setAttribute('aria-pressed', state[g]?.has?.(id) ? 'true' : 'false');
    } else if (el.type === 'checkbox') {
      el.checked = !!state[g]?.has?.(id);
    }
  });
  document.querySelectorAll('[data-flag-id]').forEach(el => {
    el.checked = !!state[el.dataset.flagId];
  });
  // Price slider visuals
  syncPriceSlider();
}
function syncPriceSlider() {
  document.querySelectorAll('[data-price-min]').forEach(e => e.textContent = state.priceMin);
  document.querySelectorAll('[data-price-max]').forEach(e => e.textContent = state.priceMax);
  document.querySelectorAll('[data-price-fill]').forEach(fill => {
    const r = priceBounds.max - priceBounds.min;
    const lp = ((state.priceMin - priceBounds.min) / r) * 100;
    const rp = 100 - ((state.priceMax - priceBounds.min) / r) * 100;
    fill.style.left = lp + '%';
    fill.style.right = rp + '%';
  });
  document.querySelectorAll('[data-price-input="min"]').forEach(i => i.value = state.priceMin);
  document.querySelectorAll('[data-price-input="max"]').forEach(i => i.value = state.priceMax);
}

// ============================================================
// EVENT HANDLERS
// ============================================================
function setupEvents() {
  // Search input
  const search = document.getElementById('ai-search');
  search.addEventListener('input', (e) => {
    state.q = e.target.value.trim();
    state.page = 1;
    render();
  });
  document.querySelectorAll('.plp-suggest .chip').forEach(c => {
    c.addEventListener('click', () => {
      state.q = c.dataset.q;
      search.value = state.q;
      state.page = 1;
      render();
    });
  });

  // Sort
  document.getElementById('plp-sort').addEventListener('change', (e) => {
    state.sort = e.target.value;
    render();
  });

  // Load more — just reveal more of the already-loaded products.
  // The full catalog is auto-fetched in the background (see autoLoadRemaining).
  document.getElementById('load-more').addEventListener('click', () => {
    state.page++;
    render();
  });

  // Show-all in filter group
  document.addEventListener('click', (e) => {
    const sa = e.target.closest('[data-fg-showall]');
    if (sa) {
      const g = sa.dataset.fgShowall;
      // Reveal all options across BOTH sidebar + mobile sheet
      document.querySelectorAll(`[data-fg-options="${g}"] .fopt[hidden]`).forEach(el => el.removeAttribute('hidden'));
      sa.remove();
    }
  });
  // Search-within-group
  document.addEventListener('input', (e) => {
    const s = e.target.closest('[data-fg-search]');
    if (s) {
      const g = s.dataset.fgSearch;
      const q = s.value.trim().toLowerCase();
      document.querySelectorAll(`[data-fg-options="${g}"] .fopt`).forEach(el => {
        const match = el.dataset.fgItem.includes(q);
        if (match) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
      });
      // hide the show-all button while searching
      const sa = document.querySelector(`[data-fg-showall="${g}"]`);
      if (sa) sa.style.display = q ? 'none' : '';
    }
  });

  // Delegated clicks for sidebar + sheet
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-action="toggle-group"]');
    if (toggle) {
      const grp = toggle.closest('.filter-group');
      grp.dataset.open = grp.dataset.open === 'true' ? 'false' : 'true';
      // CSS only shows body when [data-open="true"]
      grp.querySelectorAll('.filter-group__body').forEach(b => {
        b.style.display = grp.dataset.open === 'true' ? 'flex' : 'none';
      });
      return;
    }
    const swatch = e.target.closest('.swatch[data-group-id]');
    if (swatch) {
      const g = swatch.dataset.groupId;
      const id = swatch.dataset.optId;
      if (state[g].has(id)) state[g].delete(id); else state[g].add(id);
      state.page = 1;
      syncFromState();
      render();
      return;
    }
    const remove = e.target.closest('[data-remove-kind]');
    if (remove) {
      const k = remove.dataset.removeKind;
      const p = remove.dataset.removePayload;
      if (k === 'q') { state.q = ''; const _s = document.getElementById('ai-search'); if (_s) _s.value = ''; }
      else if (k === 'price') { state.priceMin = priceBounds.min; state.priceMax = priceBounds.max; }
      else if (k === 'flags') { state[p] = false; }
      else if (k === 'room' || k === 'mood') { state[k] = null; }
      else if (state[k]?.delete) { state[k].delete(p); }
      state.page = 1;
      syncFromState();
      render();
      return;
    }
    if (e.target.closest('[data-action="clear-filters"]')) {
      resetFilters();
      return;
    }
    const fab = e.target.closest('#filter-fab');
    if (fab) { openSheet(); return; }
    if (e.target.closest('[data-action="close-filter-sheet"]') || e.target.closest('#filter-scrim')) {
      closeSheet(); return;
    }
    const qa = e.target.closest('[data-quick-add]');
    if (qa) {
      e.preventDefault();
      const p = allProducts.find(x => x.id === qa.dataset.quickAdd);
      if (!p) return;
      const v = findVariant(p, { size: 'M', material: 'paper' });
      cart.add({
        key:       `${p.id}-${v?.id || 'novariant'}`,
        variantId: v?.id || null,
        title: p.title, image: p.image,
        price: v?.price ?? p.price, qty: 1,
        size: v?.sizeCode || 'M', material: v?.materialCode || 'paper',
      });
      if (window.openCart) window.openCart();
    }
  });

  // Delegated change for checkbox filters + flags + price
  document.addEventListener('change', (e) => {
    const opt = e.target.closest('[data-group-id]');
    if (opt && opt.type === 'checkbox') {
      const g = opt.dataset.groupId;
      const id = opt.dataset.optId;
      if (e.target.checked) state[g].add(id); else state[g].delete(id);
      state.page = 1;
      syncFromState();
      render();
      return;
    }
    const flag = e.target.closest('[data-flag-id]');
    if (flag) {
      state[flag.dataset.flagId] = e.target.checked;
      state.page = 1;
      syncFromState();
      render();
      return;
    }
  });
  document.addEventListener('input', (e) => {
    const p = e.target.closest('[data-price-input]');
    if (p) {
      const which = p.dataset.priceInput;
      const v = Number(p.value);
      if (which === 'min') state.priceMin = Math.min(v, state.priceMax - 1);
      else                 state.priceMax = Math.max(v, state.priceMin + 1);
      state.page = 1;
      syncPriceSlider();
      render();
    }
  });
}

function resetFilters() {
  state.q = '';
  state.categories.clear(); state.styles.clear(); state.colors.clear(); state.room = null; state.mood = null;
  state.sizes.clear(); state.materials.clear();
  state.priceMin = priceBounds.min; state.priceMax = priceBounds.max;
  state.onSale = false; state.isNew = false; state.page = 1;
  document.getElementById('ai-search').value = '';
  syncFromState();
  render();
}

// ============================================================
// MOBILE SHEET
// ============================================================
function openSheet()  {
  document.getElementById('filter-sheet').classList.add('is-open');
  document.getElementById('filter-scrim').classList.add('is-open');
  document.body.style.overflow = 'hidden';
}
function closeSheet() {
  document.getElementById('filter-sheet').classList.remove('is-open');
  document.getElementById('filter-scrim').classList.remove('is-open');
  document.body.style.overflow = '';
}

// ============================================================
// URL STATE — shareable links + browser back/forward
// ============================================================
function writeUrlState() {
  const u = new URL(location.href);
  u.search = '';
  if (state.q) u.searchParams.set('q', state.q);
  if (state.categories.size) u.searchParams.set('cat', [...state.categories].join(','));
  if (state.styles.size)     u.searchParams.set('style', [...state.styles].join(','));
  if (state.colors.size)     u.searchParams.set('color', [...state.colors].join(','));
  if (state.room) u.searchParams.set('room', state.room); else u.searchParams.delete('room');
  if (state.mood) u.searchParams.set('mood', state.mood); else u.searchParams.delete('mood');
  if (state.priceMin !== priceBounds.min) u.searchParams.set('pmin', state.priceMin);
  if (state.priceMax !== priceBounds.max) u.searchParams.set('pmax', state.priceMax);
  if (state.onSale) u.searchParams.set('sale', '1');
  if (state.isNew)  u.searchParams.set('new', '1');
  if (state.sort !== 'recommended') u.searchParams.set('sort', state.sort);
  history.replaceState(null, '', u.toString());
}
function readUrlState() {
  const u = new URL(location.href);
  const gp = (k) => u.searchParams.get(k);
  state.q = gp('q') || '';
  document.getElementById('ai-search').value = state.q;
  if (gp('cat'))   gp('cat').split(',').forEach(x => state.categories.add(x));
  if (gp('style')) gp('style').split(',').forEach(x => state.styles.add(x));
  if (gp('color')) gp('color').split(',').forEach(x => state.colors.add(x));
  if (gp('room') && roomById(gp('room'))) state.room = gp('room');
  if (gp('mood') && moodById(gp('mood'))) state.mood = gp('mood');
  if (gp('pmin'))  state.priceMin = Number(gp('pmin'));
  if (gp('pmax'))  state.priceMax = Number(gp('pmax'));
  state.onSale = gp('sale') === '1';
  state.isNew  = gp('new') === '1';
  if (gp('sort')) {
    state.sort = gp('sort');
    document.getElementById('plp-sort').value = state.sort;
  }
  // legacy ?c=... category (from old links)
  const legacyC = gp('c');
  if (legacyC && legacyC !== 'collecties') state.categories.add(legacyC);
}

// ============================================================
// INIT
// ============================================================
function updateHeadCount() {
  const countSubEl = document.getElementById('plp-count');
  // Alleen het aantal bijwerken: de (vertaalde) tekst komt uit de server-render.
  const num = countSubEl && countSubEl.querySelector('.tabular');
  if (num) num.textContent = allProducts.length;
}

function recalcPriceBounds() {
  const prices = allProducts.map(p => p.price).filter(p => typeof p === 'number' && !isNaN(p));
  if (prices.length > 0) {
    priceBounds.min = Math.floor(Math.min(...prices));
    priceBounds.max = Math.ceil(Math.max(...prices, 50));
  }
}

// Load the bulk all-products.json from a Shopify asset (gzipped, CDN-cached, ~1MB).
// Single request returns whole catalog with metafields. Replaces allProducts.
async function loadFullCatalog() {
  const t0 = performance.now();
  const full = await loadAllProducts();
  if (!full || full.length === 0) return;

  // For a SPECIFIC collection page, filter to that collection's products.
  // For /collections/all (or unknown), keep all products.
  //
  // Filteren op ECHTE collectie-lidmaatschap via Shopify's eigen
  // /collections/{handle}/products.json (werkt in elke locale). De oude
  // titel-matching vergeleek tegen een variabele die nooit gezet werd,
  // waardoor elke categoriepagina stilletjes de volledige catalogus toonde
  // (bug 02-09: "Mythology" toonde 6998 voetballers).
  const collHandle = (window.SHOPIFY_COLLECTION_HANDLE || '').toLowerCase();
  let filtered = full;
  if (collHandle && collHandle !== 'all' && collHandle !== '') {
    if (collHandle === 'bestsellers') {
      filtered = full.filter(p => p.bestsellerRank < 9999);
    } else if (full.some(p => (p.collectionIds || p.collectionHandles || []).length)) {
      // Lidmaatschap zit in de catalogus-asset zelf. Filteren op collectie-ID:
      // handles zijn per taal vertaald (EN 'minimalist' vs NL 'minimalistisch')
      // en matchen dus niet in elke locale; het ID is stabiel.
      const collId = Number(window.SHOPIFY_COLLECTION_ID || 0);
      filtered = full.filter(p =>
        (collId && (p.collectionIds || []).includes(collId)) ||
        (p.collectionHandles || []).includes(collHandle));
      if (!filtered.length) filtered = full;
    } else {
      try {
        const leden = new Set();
        for (let page = 1; page <= 12; page++) {
          const r = await fetch(`/collections/${collHandle}/products.json?limit=250&page=${page}`);
          if (!r.ok) break;
          const j = await r.json();
          (j.products || []).forEach(p => leden.add(p.handle));
          if ((j.products || []).length < 250) break;
        }
        if (leden.size > 0) filtered = full.filter(p => leden.has(p.handle));
      } catch (e) { /* endpoint onbereikbaar: dan liever alles dan een lege pagina */ }
    }
  }
  // 'Nieuw binnen' = de 48 laatst toegevoegde designs (Wes 03-09)
  const opDatum = [...full].filter(x => x.publishedAt).sort((a, b) => b.publishedAt - a.publishedAt).slice(0, 48);
  const nieuwSet = new Set(opDatum.map(x => x.handle));
  filtered.forEach(x => { x.isNew = nieuwSet.has(x.handle); });
  allProducts = filtered;
  catalogusKlaar = true;
  registerQuickPool(allProducts);
  recalcPriceBounds();
  renderGroups();
  updateHeadCount();
  render();
  console.log(`[plp.js] full catalog ready: ${allProducts.length} products, nieuw=${allProducts.filter(p=>p.isNew).length}, sale=${allProducts.filter(p=>p.onSale).length} (${(performance.now()-t0).toFixed(0)}ms total)`);
}

(async () => {
  // Early-bail: /search en /collections (list) laden dit script ook (template
  // bevat 'collection'/'search') maar zijn volledig server-rendered zonder
  // PLP-shell. Zonder deze guard crasht readUrlState op ontbrekende elementen.
  if (!document.getElementById('ai-search') || !document.getElementById('plp-grid')) {
    console.log('[plp.js] geen interactieve PLP-shell op deze pagina, skip init');
    return;
  }
  allProducts = await loadProducts();
  registerQuickPool(allProducts);
  console.log('[plp.js] inline loaded:', allProducts.length, '/', window.SHOPIFY_COLLECTION_SIZE || '?', 'total');

  recalcPriceBounds();
  state.priceMin = priceBounds.min;
  state.priceMax = priceBounds.max;
  updateHeadCount();

  readUrlState();
  renderGroups();
  setupEvents();
  // Op een specifieke collectiepagina NIET eerst de ongefilterde set renderen
  // (dat gaf 2 seconden verkeerde posters); de SSR-kaarten blijven staan tot
  // de gefilterde catalogus klaar is.
  const _ch = (window.SHOPIFY_COLLECTION_HANDLE || '').toLowerCase();
  if (!_ch || _ch === 'all' || _ch === '') render();

  // Volledige catalogus (1,3 MB) pas NA de eerste weergave laden: bij de eerste interactie
  // (scrollen, tikken, toets) of zodra de browser na 'load' tijd over heeft, uiterlijk na 4 s.
  // 26-09-2026: direct laden (plus een preload in de head) hield de hoofdthread 7-11 s bezig op
  // mobiel; de 36 server-side kaarten dekken de eerste weergave. Lighthouse LCP 11,8 s.
  let _geladen = false;
  const _start = () => {
    if (_geladen) return; _geladen = true;
    ['scroll', 'pointerdown', 'keydown', 'touchstart', 'click', 'change', 'focusin'].forEach(ev => removeEventListener(ev, _start));
    loadFullCatalog();
  };
  ['scroll', 'pointerdown', 'keydown', 'touchstart', 'click', 'change', 'focusin'].forEach(ev => addEventListener(ev, _start, { passive: true, once: true }));
  window.__plpStartCatalogus = _start;
  const _idle = () => ('requestIdleCallback' in window) ? requestIdleCallback(_start, { timeout: 4000 }) : setTimeout(_start, 2500);
  // Binnengekomen mét filters (Posterzoeker, gedeelde link): meteen laden, er staat nu een laadstatus.
  if (activeFilterCount() > 0) _start();
  // 13-10-2026: niet meer vanzelf na het laden (idle). De 36 server-kaarten dekken de eerste weergave; de catalogus
  // (27 MB) komt pas bij de eerste aanraking (scrollen, tikken, toets). Vanzelf laden verving het raster na 4-13 s
  // en telde dan als "grootste element"; wie niets aanraakt hoeft de catalogus ook niet te downloaden.
  void _idle;
})();
